import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Panel from '../Panel';
import Break from '../Break';
import { BlueBorderButton, BlueButton } from 'components/Button';
import {
  NO_CONTROL_OPTION_VALUE,
  CONTROL_TYPES,
  ERROR_MESSAGES,
  DEFAULT_CONTROL_FIELD_UNIT,
  DEFAULT_CONTROL_FIELDS_LIST_ORDER
} from 'utils/appConstants';
import { updateToponodeDefaultControl } from '../../reducers/toponode/actions';
import { revertSidePanel } from '../../reducers/sidePanel/actions';
import { updateIndenodeDefaultControl } from '../../reducers/indenode/actions';
import ControlType from 'components/ControlType';
import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowDisplay,
  PanelRowTextForm,
  PanelButtonRow,
  LabledInput,
  MultipleInputRowContainer,
  InputUnitLabel,
  InputAndLabelContainer,
  StyledForm,
  PanelContent,
  FullPanel
} from 'components/PanelComponents';
import Text from 'components/Text';
import { create_UUID } from 'utils/uuidGenerator';
import { convertTohhmmss, convertToSeconds } from 'utils/format';

const curveIdControlTypes = [
  'opModFreqWattCurveId',
  'opModHFRTMayTripCurveId',
  'opModHFRTMustTripCurveId',
  'opModHVRTMayTripCurveId',
  'opModHVRTMomentaryCessationCurveId',
  'opModHVRTMustTripCurveId',
  'opModLFRTMayTripCurveId',
  'opModLFRTMustTripCurveId',
  'opModLVRTMayTripCurveId',
  'opModLVRTMomentaryCessationCurveId',
  'opModLVRTMustTripCurveId',
  'opModVoltVarCurveId',
  'opModVoltWattCurveId',
  'opModWattPFCurveId',
  'opModWattVarCurveId'
];

const EditNodeDefaultControl = ({ isActive }) => {
  const dispatch = useDispatch();
  const topoNodes = useSelector(state => state.topoNode.topoNodes);
  const indenodes = useSelector(state => state.indeNode.indeNodes);
  const sidePanel = useSelector(state => state.sidePanel);
  const parentIndeNodeId = useSelector(
    state => state?.sidePanel?.data?.parentIndeNodeId
  );
  const parentTopoNodeId = useSelector(
    state => state?.sidePanel?.data?.parentTopoNodeId
  );

  const curves = useSelector(state => state.curve.curves);
  const [description, setDescription] = useState('');
  const [setESHighFreq, setSetESHighFreq] = useState('');
  const [setESDelay, setSetESDelay] = useState('');
  const [setESDelayHours, setSetESDelayHours] = useState('');
  const [setESDelayMinutes, setSetESDelayMinutes] = useState('');
  const [setESDelaySeconds, setSetESDelaySeconds] = useState('');
  const [setESHighVolt, setSetESHighVolt] = useState('');
  const [setESLowFreq, setSetESLowFreq] = useState('');
  const [setESLowVolt, setSetESLowVolt] = useState('');
  const [setESRampTms, setSetESRampTms] = useState('');
  const [setESRampTmsHours, setSetESRampTmsHours] = useState('');
  const [setESRampTmsMinutes, setSetESRampTmsMinutes] = useState('');
  const [setESRampTmsSeconds, setSetESRampTmsSeconds] = useState('');
  const [setESRandomDelay, setSetESRandomDelay] = useState('');
  const [setESRandomDelayHours, setSetESRandomDelayHours] = useState('');
  const [setESRandomDelayMinutes, setSetESRandomDelayMinutes] = useState('');
  const [setESRandomDelaySeconds, setSetESRandomDelaySeconds] = useState('');
  const [setGradW, setSetGradW] = useState('');
  const [setSoftGradW, setSetSoftGradW] = useState('');
  const [controlTypes, setControlTypes] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waitingReturn, setWaitingReturn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [node, setNode] = useState({});
  const [parentIndeNode, setParentIndeNode] = useState(null);
  const [parentTopoNode, setParentTopoNode] = useState(null);

  useEffect(() => {
    const { WRONG_ENTITY_TYPE } = ERROR_MESSAGES;

    //node{} will be the parent node who holds this default control.
    //Determine if it was a toponode or indenode
    let node = {};
    if (parentTopoNodeId !== undefined) {
      node = topoNodes.find(
        topoNode => topoNode.topoNodeId === parentTopoNodeId
      );
    } else if (parentIndeNodeId !== undefined) {
      node = indenodes.find(
        indeNode => indeNode.indeNodeId === parentIndeNodeId
      );
    } else {
      throw new Error(WRONG_ENTITY_TYPE);
    }

    //Gather the default control from that node directly from the redux store.
    const { defaultControl } = node;

    //set all controlTypes (such as opModTargetW) in their own object.
    //Make them ready to be displayed/friendly
    const controlTypes = defaultControl.controlTypes || {};
    const setControlsFriendly = Object.keys(controlTypes).map(controlType => ({
      name: CONTROL_TYPES[controlType].name,
      abbreviation: controlType
    }));
    const renderedControlTypes = [];
    for (let i = 0; i < setControlsFriendly.length; i++) {
      let obj = {};
      obj.controlType = setControlsFriendly[i].abbreviation;
      const { controlType } = obj;
      obj.key = create_UUID();
      switch (controlType) {
        //boolean types
        case 'opModConnect':
        case 'opModEnergize':
          if (controlTypes[controlType] === true) {
            obj[controlType] = '1';
          } else {
            obj[controlType] = '0';
          }
          break;

        //curve types
        case 'opModFreqWattCurveId':
        case 'opModHFRTMayTripCurveId':
        case 'opModHFRTMustTripCurveId':
        case 'opModHVRTMayTripCurveId':
        case 'opModHVRTMomentaryCessationCurveId':
        case 'opModHVRTMustTripCurveId':
        case 'opModLFRTMayTripCurveId':
        case 'opModLFRTMustTripCurveId':
        case 'opModLVRTMayTripCurveId':
        case 'opModLVRTMomentaryCessationCurveId':
        case 'opModLVRTMustTripCurveId':
        case 'opModVoltVarCurveId':
        case 'opModVoltWattCurveId':
        case 'opModWattPFCurveId':
        case 'opModWattVarCurveId':
          obj.curve = curves.find(
            curve => curve.curveId === controlTypes[controlType]
          );
          break;

        //boolean & value types
        case 'opModFixedPFInjectW':
          obj.displacement = controlTypes[controlType].displacement;
          obj.multiplier = controlTypes[controlType].multiplier;
          if (controlTypes[controlType].excitation === true) {
            obj.pfinjectExcitation = '1';
          } else {
            obj.pfinjectExcitation = '0';
          }
          break;
        case 'opModFixedPFAbsorbW':
          obj.displacement = controlTypes[controlType].displacement;
          obj.multiplier = controlTypes[controlType].multiplier;
          if (controlTypes[controlType].excitation === true) {
            obj.pfabsorbExcitation = '1';
          } else {
            obj.pfabsorbExcitation = '0';
          }
          break;

        case 'opModFreqDroop':
          obj.dBOF = controlTypes[controlType].dBOF / 1000;
          obj.dBUF = controlTypes[controlType].dBUF / 1000;
          obj.kOF = controlTypes[controlType].kOF / 1000;
          obj.kUF = controlTypes[controlType].kUF / 1000;
          obj.openLoopTms = controlTypes[controlType].openLoopTms / 100;
          break;
        //Need to be reduced by 100 values
        case 'opModFixedVar':
          obj.refType = controlTypes[controlType].refType;
          obj.value = controlTypes[controlType].value / 100;
          break;
        case 'opModMaxLimW':
        case 'opModFixedW':
        case 'rampTms':
          obj[controlType] = controlTypes[controlType] / 100;
          break;
        //normal types
        case 'opModTargetW':
        case 'opModTargetVar':
          obj.multiplier = controlTypes[controlType].multiplier;
          obj.value = controlTypes[controlType].value;
          break;
        default:
          obj[controlType] = controlTypes[controlType];
          break;
      }

      renderedControlTypes.push(obj);
    }

    const { description } = defaultControl;

    const {
      setESHighFreq,
      setESDelay,
      setESHighVolt,
      setESLowFreq,
      setESLowVolt,
      setESRampTms,
      setESRandomDelay,
      setGradW,
      setSoftGradW
    } = defaultControl;

    const convertedSetESDelay = convertTohhmmss(setESDelay / 100);
    const convertedSetESRampTms = convertTohhmmss(setESRampTms / 100);
    const convertedSetESRandomDelay = convertTohhmmss(setESRandomDelay / 100);

    setDescription(description);
    setSetESHighFreq(setESHighFreq / 100);
    setSetESDelay(setESDelay / 100);
    setSetESDelayHours(convertedSetESDelay.hours);
    setSetESDelayMinutes(convertedSetESDelay.minutes);
    setSetESDelaySeconds(convertedSetESDelay.seconds);
    setSetESHighVolt(setESHighVolt / 100);
    setSetESLowFreq(setESLowFreq / 100);
    setSetESLowVolt(setESLowVolt / 100);
    setSetESRampTms(setESRampTms / 100);
    setSetESRampTmsHours(convertedSetESRampTms.hours);
    setSetESRampTmsMinutes(convertedSetESRampTms.minutes);
    setSetESRampTmsSeconds(convertedSetESRampTms.seconds);
    setSetESRandomDelay(setESRandomDelay / 100);
    setSetESRandomDelayHours(convertedSetESRandomDelay.hours);
    setSetESRandomDelayMinutes(convertedSetESRandomDelay.minutes);
    setSetESRandomDelaySeconds(convertedSetESRandomDelay.seconds);
    setSetGradW(setGradW / 100);
    setSetSoftGradW(setSoftGradW / 100);
    setControlTypes(renderedControlTypes);
    setIsLoading(false);
    setNode(node);
    setParentIndeNode(parentIndeNodeId);
    setParentTopoNode(parentTopoNodeId);
  }, []);

  useEffect(() => {
    if (isActive && waitingReturn !== null) {
      if (sidePanel?.data?.curveId !== undefined) {
        const { curveId } = sidePanel.data;
        const curve = curves.find(curve => curve.curveId === curveId);
        const updatedControlTypes = controlTypes.slice();
        updatedControlTypes[waitingReturn] = {
          ...updatedControlTypes[waitingReturn],
          curve
        };
        setControlTypes(updatedControlTypes);
        setWaitingReturn(null);
      }
    }
  }, [
    controlTypes,
    curves,
    isActive,
    setWaitingReturn,
    sidePanel.data,
    waitingReturn
  ]);

  const handleInputChange = event => {
    event.stopPropagation();
    const target = event.target;
    const value =
      target.value === NO_CONTROL_OPTION_VALUE ? null : target.value;
    const name = target.name;
    if (name === 'description') {
      setDescription(value);
    } else if (name === 'setESDelayHours') {
      setSetESDelayHours(value);
    } else if (name === 'setESDelayMinutes') {
      setSetESDelayMinutes(value);
    } else if (name === 'setESDelaySeconds') {
      setSetESDelaySeconds(value);
    } else if (name === 'setESHighFreq') {
      setSetESHighFreq(value);
    } else if (name === 'setESHighVolt') {
      setSetESHighVolt(value);
    } else if (name === 'setESLowFreq') {
      setSetESLowFreq(value);
    } else if (name === 'setESLowVolt') {
      setSetESLowVolt(value);
    } else if (name === 'setESRampTmsHours') {
      setSetESRampTmsHours(value);
    } else if (name === 'setESRampTmsMinutes') {
      setSetESRampTmsMinutes(value);
    } else if (name === 'setESRampTmsSeconds') {
      setSetESRampTmsSeconds(value);
    } else if (name === 'setESRandomDelayHours') {
      setSetESRandomDelayHours(value);
    } else if (name === 'setESRandomDelayMinutes') {
      setSetESRandomDelayMinutes(value);
    } else if (name === 'setESRandomDelaySeconds') {
      setSetESRandomDelaySeconds(value);
    } else if (name === 'setGradW') {
      setSetGradW(value);
    } else if (name === 'setSoftGradW') {
      setSetSoftGradW(value);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    e.persist();
    setIsSubmitting(true);

    const controlsObj = {};
    for (let i = 0; i < controlTypes.length; i++) {
      let controlType = controlTypes[i].controlType;
      if (controlType === NO_CONTROL_OPTION_VALUE) {
        setErrors({ ...errors, [controlType]: true });
        setIsSubmitting(false);
        return e.preventDefault();
      }

      // handle true/false forms
      if (controlType === 'opModConnect' || controlType === 'opModEnergize') {
        controlsObj[controlType] = !!parseInt(controlTypes[i][controlType]);
      }

      if (
        controlType === 'opModFixedPFAbsorbW' ||
        controlType === 'opModFixedPFInjectW'
      ) {
        let excitation;
        const { displacement, multiplier } = controlTypes[i];
        if (controlType === 'opModFixedPFAbsorbW') {
          excitation = controlTypes[i].pfabsorbExcitation;
        } else {
          excitation = controlTypes[i].pfinjectExcitation;
        }
        controlsObj[controlType] = {
          displacement: parseInt(displacement),
          excitation: !!parseInt(excitation),
          multiplier: parseInt(multiplier)
        };
      }

      if (controlType === 'opModFixedVar') {
        const { refType, value } = controlTypes[i];
        controlsObj[controlType] = {
          refType: parseInt(refType),
          value: Math.round(parseFloat(value) * 100)
        };
      }

      // handle curve ids
      if (curveIdControlTypes.indexOf(controlType) > -1) {
        if (controlTypes[i].curve) {
          controlsObj[controlType] = controlTypes[i].curve.curveId;
        } else {
          setErrors({ ...errors, [controlType]: true });
          setIsSubmitting(false);

          return e.preventDefault();
        }
      }

      if (
        controlType === 'opModMaxLimW' ||
        controlType === 'opModFixedW' ||
        controlType === 'rampTms'
      ) {
        // opModMaxLimW (0 - 10000)
        // opModFixedW (-10000 - 10000)
        // rampTms (0 - 65535)
        controlsObj[controlType] = Math.round(
          parseFloat(controlTypes[i][controlType]) * 100
        );
      }

      if (controlType === 'opModTargetW' || controlType === 'opModTargetVar') {
        const { value, multiplier } = controlTypes[i];
        controlsObj[controlType] = {
          value: parseInt(value),
          multiplier: parseInt(multiplier)
        };
      }

      if (controlType === 'opModFreqDroop') {
        const { dBOF, dBUF, kOF, kUF, openLoopTms } = controlTypes[i];
        controlsObj[controlType] = {
          dBOF: Math.round(parseFloat(dBOF) * 1000),
          dBUF: Math.round(parseFloat(dBUF) * 1000),
          kOF: Math.round(parseFloat(kOF) * 1000),
          kUF: Math.round(parseFloat(kUF) * 1000),
          openLoopTms: Math.round(parseFloat(openLoopTms) * 100)
        };
      }
    }

    const requestObj = {
      ...controlsObj
    };

    DEFAULT_CONTROL_FIELDS_LIST_ORDER.forEach(defaultControlItem => {
      //string types
      if (
        defaultControlItem === 'description' &&
        description !== null &&
        description !== undefined &&
        description !== ''
      ) {
        requestObj['description'] = description;
      }
      //int types
      else {
        const controlItems = {
          setESDelay,
          setESHighFreq,
          setESHighVolt,
          setESLowFreq,
          setESLowVolt,
          setESRampTms,
          setESRandomDelay,
          setGradW,
          setSoftGradW
        };
        let valueToParse = controlItems[defaultControlItem];

        //default control special conversions
        if (
          defaultControlItem === 'setESDelay' &&
          (setESDelayHours || setESDelayMinutes || setESDelaySeconds)
        ) {
          valueToParse = convertToSeconds({
            hours: parseInt(setESDelayHours),
            minutes: parseInt(setESDelayMinutes),
            seconds: parseInt(setESDelaySeconds)
          });
          requestObj[defaultControlItem] = valueToParse * 100;
        } else if (
          defaultControlItem === 'setESRampTms' &&
          (setESRampTmsHours || setESRampTmsMinutes || setESRampTmsSeconds)
        ) {
          valueToParse = convertToSeconds({
            hours: parseInt(setESRampTmsHours),
            minutes: parseInt(setESRampTmsMinutes),
            seconds: parseInt(setESRampTmsSeconds)
          });
          requestObj[defaultControlItem] = valueToParse * 100;
        } else if (
          defaultControlItem === 'setESRandomDelay' &&
          (setESRandomDelayHours ||
            setESRandomDelayMinutes ||
            setESRandomDelaySeconds)
        ) {
          valueToParse = convertToSeconds({
            hours: parseInt(setESRandomDelayHours),
            minutes: parseInt(setESRandomDelayMinutes),
            seconds: parseInt(setESRandomDelaySeconds)
          });
          requestObj[defaultControlItem] = valueToParse * 100;
        } else if (valueToParse) {
          //currently all default control properties work in hundredths
          const value = Math.round(parseFloat(valueToParse * 100));
          if (!Number.isNaN(value)) {
            requestObj[defaultControlItem] = value;
          }
        }
      }
    });
    try {
      if (parentTopoNode !== undefined) {
        await dispatch(
          updateToponodeDefaultControl(parentTopoNode, requestObj)
        );
        dispatch(revertSidePanel());
      } else if (parentIndeNode !== undefined) {
        await dispatch(
          updateIndenodeDefaultControl(parentIndeNode, requestObj)
        );
        dispatch(revertSidePanel());
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormChange = obj => {
    const { index, key, value } = obj;
    // This slice is used often to avoid mutation of current state when setting state
    const updatedControlTypes = controlTypes.slice();
    updatedControlTypes[index][key] = value;
    setControlTypes(updatedControlTypes);
  };

  const addControlType = () => {
    const updatedControlTypes = controlTypes.slice();
    updatedControlTypes.push({
      controlType: NO_CONTROL_OPTION_VALUE,
      key: new Date().getTime()
    });
    setControlTypes(updatedControlTypes);
    setErrors({
      ...errors,
      controlTypes: false
    });
  };

  const removeControlType = index => {
    const curveName = controlTypes[index].controlType;
    const updatedControlTypes = controlTypes.slice();
    updatedControlTypes.splice(index, 1);
    const updatedErrors = { ...errors };
    delete updatedErrors[curveName];
    setControlTypes(updatedControlTypes);
    setErrors(updatedErrors);
  };

  const handleControlTypeSelect = (event, index) => {
    const curveName = controlTypes[index].controlType;
    const updatedControlTypes = controlTypes.slice();
    const { key } = updatedControlTypes[index];
    updatedControlTypes[index] = {
      controlType: event.target.value,
      key
    };
    const updatedErrors = { ...errors };
    delete updatedErrors[curveName];
    setControlTypes(updatedControlTypes);
    setErrors(updatedErrors);
  };

  const removeCurve = index => {
    const updatedControlTypes = controlTypes.slice();
    delete updatedControlTypes[index].curve;
    setControlTypes(updatedControlTypes);
  };

  const setWaitingForReturn = index => {
    const curveName = controlTypes[index].controlType;
    const updatedErrors = { ...errors };
    delete updatedErrors[curveName];
    setWaitingReturn(index);
    setErrors(updatedErrors);
  };

  if (!isActive || (isActive && isLoading) || node === undefined) {
    return null;
  } else {
    const takenControls = controlTypes.map(obj => obj.controlType);

    return (
      <StyledForm onSubmit={handleSubmit}>
        <FullPanel>
          <Panel title="Selected Node » Default Control" primaryHeader />
          <PanelContent>
            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">Description</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <PanelRowTextForm
                  name="description"
                  onChange={handleInputChange}
                  isError={errors.description}
                  value={description ? description : ''}
                  maxLength="32"
                />
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">setESDelay</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>

                {/* hours */}
                <MultipleInputRowContainer>
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="setESDelayHours"
                      value={setESDelayHours}
                      onChange={handleInputChange}
                      // onBlur={this.durationChangeBlur}
                      min="0"
                      max="11929"
                      isError={errors.setESDelay}
                    />
                    <InputUnitLabel>hrs</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* minutes */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="setESDelayMinutes"
                      value={setESDelayMinutes}
                      onChange={handleInputChange}
                      // onBlur={this.durationChangeBlur}
                      min="0"
                      max="60"
                      isError={errors.setESDelay}
                    />
                    <InputUnitLabel>min</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* seconds */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="setESDelaySeconds"
                      value={setESDelaySeconds}
                      onChange={handleInputChange}
                      // onBlur={this.durationChangeBlur}
                      min="0"
                      max="60"
                      isError={errors.setESDelay}
                    />
                    <InputUnitLabel>sec</InputUnitLabel>
                  </InputAndLabelContainer>
                </MultipleInputRowContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">setESHighFreq</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <InputAndLabelContainer>
                  <LabledInput
                    type="number"
                    name="setESHighFreq"
                    onChange={handleInputChange}
                    isError={errors.setESHighFreq}
                    min="0"
                    max="655.35"
                    step="0.01"
                    value={setESHighFreq}
                  />
                  <InputUnitLabel>
                    {DEFAULT_CONTROL_FIELD_UNIT.setESHighFreq}
                  </InputUnitLabel>
                </InputAndLabelContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">setESHighVolt</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <InputAndLabelContainer>
                  <LabledInput
                    type="number"
                    value={setESHighVolt}
                    name="setESHighVolt"
                    onChange={handleInputChange}
                    isError={errors.setESHighVolt}
                    min="-327.68"
                    max="327.67"
                    step="0.01"
                  />
                  <InputUnitLabel>
                    {DEFAULT_CONTROL_FIELD_UNIT.setESHighVolt}
                  </InputUnitLabel>
                </InputAndLabelContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">setESLowFreq</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <InputAndLabelContainer>
                  <LabledInput
                    type="number"
                    value={setESLowFreq}
                    name="setESLowFreq"
                    onChange={handleInputChange}
                    isError={errors.setESLowFreq}
                    min="0"
                    max="655.35"
                    step="0.01"
                  />
                  <InputUnitLabel>
                    {DEFAULT_CONTROL_FIELD_UNIT.setESLowFreq}
                  </InputUnitLabel>
                </InputAndLabelContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">setESLowVolt</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <InputAndLabelContainer>
                  <LabledInput
                    type="number"
                    value={setESLowVolt}
                    name="setESLowVolt"
                    onChange={handleInputChange}
                    isError={errors.setESLowVolt}
                    min="-327.68"
                    max="327.67"
                    step="0.01"
                  />
                  <InputUnitLabel>
                    {DEFAULT_CONTROL_FIELD_UNIT.setESLowVolt}
                  </InputUnitLabel>
                </InputAndLabelContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">setESRampTms</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>

                <MultipleInputRowContainer>
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="setESRampTmsHours"
                      value={setESRampTmsHours}
                      onChange={handleInputChange}
                      // onBlur={this.durationChangeBlur}
                      min="0"
                      max="11929"
                      isError={errors.setESRampTms}
                    />
                    <InputUnitLabel>hrs</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* minutes */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="setESRampTmsMinutes"
                      value={setESRampTmsMinutes}
                      onChange={handleInputChange}
                      // onBlur={this.durationChangeBlur}
                      min="0"
                      max="60"
                      isError={errors.setESRampTms}
                    />
                    <InputUnitLabel>min</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* seconds */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="setESRampTmsSeconds"
                      value={setESRampTmsSeconds}
                      onChange={handleInputChange}
                      // onBlur={this.durationChangeBlur}
                      min="0"
                      max="60"
                      isError={errors.setESRampTms}
                    />
                    <InputUnitLabel>sec</InputUnitLabel>
                  </InputAndLabelContainer>
                </MultipleInputRowContainer>
              </SubPanelRow>
              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">setESRandomDelay</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>

                <MultipleInputRowContainer>
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="setESRandomDelayHours"
                      value={setESRandomDelayHours}
                      onChange={handleInputChange}
                      // onBlur={this.durationChangeBlur}
                      min="0"
                      max="11929"
                      isError={errors.setESRandomDelay}
                    />
                    <InputUnitLabel>hrs</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* minutes */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="setESRandomDelayMinutes"
                      value={setESRandomDelayMinutes}
                      onChange={handleInputChange}
                      // onBlur={this.durationChangeBlur}
                      min="0"
                      max="60"
                      isError={errors.setESRandomDelay}
                    />
                    <InputUnitLabel>min</InputUnitLabel>
                  </InputAndLabelContainer>
                  {/* seconds */}
                  <InputAndLabelContainer width="30%">
                    <LabledInput
                      type="number"
                      name="setESRandomDelaySeconds"
                      value={setESRandomDelaySeconds}
                      onChange={handleInputChange}
                      // onBlur={this.durationChangeBlur}
                      min="0"
                      max="60"
                      isError={errors.setESRandomDelay}
                    />
                    <InputUnitLabel>sec</InputUnitLabel>
                  </InputAndLabelContainer>
                </MultipleInputRowContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">setGradW</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <InputAndLabelContainer>
                  <LabledInput
                    type="number"
                    value={setGradW}
                    name="setGradW"
                    onChange={handleInputChange}
                    isError={errors.setGradW}
                    min="0"
                    max="655.35"
                    step="0.01"
                  />
                  <InputUnitLabel>
                    {DEFAULT_CONTROL_FIELD_UNIT.setGradW}
                  </InputUnitLabel>
                </InputAndLabelContainer>
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">setSoftGradW</Text>
                  <Text fontSize="12px">(Optional)</Text>
                </PanelRowDescriptor>
                <InputAndLabelContainer>
                  <LabledInput
                    type="number"
                    value={setSoftGradW}
                    name="setSoftGradW"
                    onChange={handleInputChange}
                    isError={errors.setSoftGradW}
                    min="0"
                    max="655.35"
                    step="0.01"
                  />
                  <InputUnitLabel>
                    {DEFAULT_CONTROL_FIELD_UNIT.setSoftGradW}
                  </InputUnitLabel>
                </InputAndLabelContainer>
              </SubPanelRow>
            </SubPanel>

            <Break margin="16px 0" />
            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>Selected Node</PanelRowDescriptor>
                <PanelRowDisplay>
                  {parentTopoNode || parentIndeNode}
                </PanelRowDisplay>
              </SubPanelRow>
            </SubPanel>

            <Break margin="16px 0" />

            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>Control Mode(s)</PanelRowDescriptor>
              </SubPanelRow>

              {controlTypes.map((obj, i) => {
                return (
                  <ControlType
                    key={obj.key}
                    controlObj={obj}
                    takenControls={takenControls}
                    handleFormChange={handleFormChange}
                    removeControlType={removeControlType}
                    removeCurve={removeCurve}
                    handleControlTypeSelect={handleControlTypeSelect}
                    //hidePanel={hidePanel}
                    index={i}
                    isError={errors[obj.controlType]}
                    setWaitingForReturn={setWaitingForReturn}
                  />
                );
              })}
            </SubPanel>

            {controlTypes.length < 23 ? (
              <SubPanel>
                <PanelButtonRow noBorder>
                  <BlueBorderButton
                    type="button"
                    onClick={addControlType}
                    isError={errors.controlTypes}
                  >
                    + Add A Control Mode
                  </BlueBorderButton>
                </PanelButtonRow>
              </SubPanel>
            ) : null}

            <Break margin="16px 0" />
          </PanelContent>
          <PanelButtonRow>
            <BlueBorderButton
              type="button"
              onClick={() => dispatch(revertSidePanel())}
            >
              Cancel
            </BlueBorderButton>
            <BlueButton type="submit" disabled={isSubmitting}>
              Save Changes
            </BlueButton>
          </PanelButtonRow>
        </FullPanel>
      </StyledForm>
    );
  }
};

export default EditNodeDefaultControl;
